/**
 * BrokerSetupWizard — guided per-broker key generation + instant validate flow.
 * Triggered from API Vault "Add Broker API Key" → walks the user through:
 *  1. Where to generate the key (exchange-specific link + numbered steps)
 *  2. Paste keys (with passphrase for KuCoin)
 *  3. Save (encrypted) + auto-validate
 *  4. "Ready to Trade" confirmation
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { X, ExternalLink, ShieldCheck, Loader2, ChevronRight, ChevronLeft, Sparkles, Lock, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

// Per-broker setup guide (links + steps + key labels)
const BROKER_GUIDES = {
  binance: {
    name: 'Binance',
    color: 'from-yellow-500 to-amber-500',
    docs_url: 'https://www.binance.com/en/my/settings/api-management',
    testnet_url: 'https://testnet.binance.vision',
    requires_passphrase: false,
    supports_testnet: true,
    steps: [
      'Visit testnet.binance.vision and click "Login with GitHub" — testnet is FREE and safe',
      'Click "Generate HMAC_SHA256 Key" → copy the API Key and Secret Key',
      '(For LIVE) Go to binance.com → Profile → API Management → Create API → Enable "Enable Reading" + "Enable Spot Trading"',
      'IMPORTANT: Whitelist your server IP if "Restrict access to trusted IPs" is enabled',
    ],
    field_labels: { api_key: 'API Key', api_secret: 'Secret Key' },
  },
  zerodha: {
    name: 'Zerodha',
    color: 'from-orange-500 to-red-500',
    docs_url: 'https://developers.kite.trade/apps',
    requires_passphrase: false,
    supports_testnet: false,
    steps: [
      'Sign up for Kite Connect at developers.kite.trade (₹2,000/month subscription required)',
      'Click "Create a new app" → name it "Anant Trading" → set redirect URL to https://kite.zerodha.com/connect/login',
      'Copy your API Key and API Secret from the app dashboard',
      'After saving keys here, click "Connect Kite" to OAuth-link your Zerodha login session',
    ],
    field_labels: { api_key: 'API Key', api_secret: 'API Secret' },
  },
  angel_one: {
    name: 'Angel One',
    color: 'from-blue-500 to-indigo-500',
    docs_url: 'https://smartapi.angelbroking.com/',
    requires_passphrase: false,
    supports_testnet: false,
    steps: [
      'Sign up at smartapi.angelbroking.com (free for Angel One clients)',
      'Click "My Apps" → Create New App → fill name + redirect URL',
      'Copy your API Key (and the client_code/password kept private — for TOTP login)',
      'Note: live trading requires a TOTP authenticator session (Phase 3 — keys saved as draft for now)',
    ],
    field_labels: { api_key: 'API Key', api_secret: 'Client Secret / Password' },
  },
  groww: {
    name: 'Groww',
    color: 'from-emerald-500 to-teal-500',
    docs_url: 'https://groww.in',
    requires_passphrase: false,
    supports_testnet: false,
    steps: [
      'Groww does NOT offer a public trading API',
      'Keys are saved only for PORTFOLIO TRACKING reference',
      'You can manually paste any identifier (e.g. your Groww username) in the fields',
      'Auto-trading via Groww is not possible — use Zerodha or Angel One instead',
    ],
    field_labels: { api_key: 'Reference ID', api_secret: 'Notes' },
    warning: 'Groww has no trading API — read-only entry for tracking.',
  },
  coindcx: {
    name: 'CoinDCX',
    color: 'from-cyan-500 to-blue-500',
    docs_url: 'https://coindcx.com/api-dashboard',
    requires_passphrase: false,
    supports_testnet: false,
    steps: [
      'Login to coindcx.com → click your profile icon → "API Dashboard"',
      'Click "Create API Key" → choose "Spot Trading" permission → confirm via email + 2FA',
      'Copy the API Key and Secret Key shown ONCE (Secret is not retrievable again)',
      'Whitelist this server\'s IP (optional but recommended for production)',
    ],
    field_labels: { api_key: 'API Key', api_secret: 'Secret Key' },
  },
  kucoin: {
    name: 'KuCoin',
    color: 'from-emerald-400 to-cyan-500',
    docs_url: 'https://www.kucoin.com/account/api',
    requires_passphrase: true,
    supports_testnet: false,
    steps: [
      'Login to kucoin.com → API Management → "Create API"',
      'Choose API name + set a PASSPHRASE (you set this — needed for every request)',
      'Enable "General + Trade" permissions; do NOT enable Transfer/Withdraw',
      'Copy the API Key, Secret, AND the Passphrase you set — all three are required',
    ],
    field_labels: { api_key: 'API Key', api_secret: 'API Secret' },
  },
  wazirx: {
    name: 'WazirX',
    color: 'from-yellow-400 to-orange-500',
    docs_url: 'https://wazirx.com/settings/keys',
    requires_passphrase: false,
    supports_testnet: false,
    steps: [
      'Login to wazirx.com → Settings → API Keys',
      'Click "Create New Key" → name it "Anant Trading" → confirm via email 2FA',
      'Enable "Trading" permission; KEEP "Withdraw" DISABLED for safety',
      'Copy the API Key and Secret Key (Secret shown only once!)',
    ],
    field_labels: { api_key: 'API Key', api_secret: 'Secret Key' },
  },
  bybit: {
    name: 'Bybit',
    color: 'from-orange-400 to-yellow-500',
    docs_url: 'https://www.bybit.com/app/user/api-management',
    requires_passphrase: false,
    supports_testnet: false,
    steps: [
      'Login to bybit.com → Profile → API → Create New Key',
      'Choose "System-generated" → enable "Contract Trade + Spot Trade" → enter Google 2FA',
      'Set "No IP restriction" or whitelist this server\'s IP',
      'Copy the API Key + Secret. Default permissions: Read + Trade (NO Withdraw)',
    ],
    field_labels: { api_key: 'API Key', api_secret: 'API Secret' },
  },
  delta_exchange: {
    name: 'Delta Exchange',
    color: 'from-purple-400 to-pink-500',
    docs_url: 'https://www.delta.exchange/app/account/manageapikeys',
    requires_passphrase: false,
    supports_testnet: false,
    steps: [
      'Login to delta.exchange → Account → API Keys → New API Key',
      'Set name + select "Trading" scope (NOT Withdraw)',
      'Confirm via 2FA → copy the API Key + Secret',
      'Whitelist your server IP if required',
    ],
    field_labels: { api_key: 'API Key', api_secret: 'API Secret' },
  },
  upstox: {
    name: 'Upstox',
    color: 'from-indigo-400 to-purple-500',
    docs_url: 'https://account.upstox.com/developer/apps',
    requires_passphrase: false,
    supports_testnet: false,
    steps: [
      'Login to account.upstox.com → Developer → Apps → Create New App',
      'Fill name + Redirect URL (must match exactly what you use)',
      'Copy API Key + API Secret',
      'For LIVE market data: admin must complete OAuth (Admin → Market Data tab)',
    ],
    field_labels: { api_key: 'API Key', api_secret: 'API Secret' },
  },
  mt5: {
    name: 'MetaTrader 5',
    color: 'from-slate-500 to-gray-500',
    docs_url: 'https://www.metatrader5.com/',
    requires_passphrase: false,
    supports_testnet: false,
    steps: [
      'MT5 trading requires a bridge service (Phase 3 — keys saved as reference for now)',
      'Get your broker server credentials from your MT5 broker (e.g. Exness, IC Markets)',
      'Paste the Login ID and Investor Password (read-only) for now',
      'Full automation requires our upcoming MT5 bridge module',
    ],
    field_labels: { api_key: 'Login ID', api_secret: 'Investor Password' },
  },
  // Fallback for any admin-added custom broker
  _generic: {
    name: 'Custom Broker',
    color: 'from-violet-500 to-purple-500',
    docs_url: '',
    requires_passphrase: false,
    supports_testnet: false,
    steps: [
      'Visit your broker\'s API/Developer portal',
      'Generate an API Key + Secret with read + trade permissions',
      'Whitelist this server\'s IP if required',
      'Paste the keys below — they will be encrypted at rest (Fernet AES-128)',
    ],
    field_labels: { api_key: 'API Key', api_secret: 'API Secret' },
  },
};

export default function BrokerSetupWizard({ broker, brokers, onClose, onSaved }) {
  // Find the broker definition (from admin-managed catalog) and merge with guide
  const brokerDef = (brokers || []).find(b => b.id === broker) || {};
  const guide = BROKER_GUIDES[broker] || {
    ...BROKER_GUIDES._generic,
    name: brokerDef.name || 'Custom Broker',
  };
  const requiresPassphrase = brokerDef.auth === 'hmac+passphrase' || guide.requires_passphrase;

  const [step, setStep] = useState(1); // 1=guide, 2=paste, 3=validating, 4=success
  const [form, setForm] = useState({
    label: '',
    api_key: '',
    api_secret: '',
    passphrase: '',
    is_testnet: !!guide.supports_testnet,
  });
  const [savedId, setSavedId] = useState(null);
  const [validation, setValidation] = useState(null);

  useEffect(() => {
    // Reset on broker change
    setStep(1);
    setSavedId(null);
    setValidation(null);
    setForm({
      label: '',
      api_key: '',
      api_secret: '',
      passphrase: '',
      is_testnet: !!guide.supports_testnet,
    });
  }, [broker, guide.supports_testnet]);

  const handleSave = async () => {
    if (!form.api_key || !form.api_secret) {
      toast.error('Both API Key and Secret are required');
      return;
    }
    if (requiresPassphrase && !form.passphrase) {
      toast.error(`${guide.name} requires a Passphrase`);
      return;
    }
    setStep(3);
    try {
      const payload = {
        broker,
        label: form.label || guide.name,
        api_key: form.api_key,
        api_secret: form.api_secret,
        is_testnet: form.is_testnet,
      };
      if (requiresPassphrase) payload.extra = { passphrase: form.passphrase };
      const r = await api.post('/algotrading/api-keys', payload);
      setSavedId(r.data.id);
      // Auto-validate
      try {
        const v = await api.post(`/algotrading/api-keys/${r.data.id}/validate`);
        setValidation(v.data);
        if (v.data.success || v.data.status === 'saved') {
          toast.success(`🎉 Ready to Trade with ${guide.name}!`);
        } else {
          toast.warning(`${guide.name} keys saved but validation said: ${v.data.message}`);
        }
      } catch (err) {
        setValidation({ success: false, status: 'failed', message: err.response?.data?.detail || 'Validation failed' });
      }
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      setStep(2);
    }
  };

  const handleFinish = () => {
    onSaved && onSaved(savedId);
    onClose && onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="broker-wizard-modal" onClick={onClose}>
      <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`bg-gradient-to-r ${guide.color} p-5 flex items-center justify-between rounded-t-2xl`}>
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-white" />
            <div>
              <div className="text-xs uppercase tracking-wider text-white/80 font-semibold">Broker Setup Wizard</div>
              <h2 className="text-xl font-bold text-white" data-testid="wizard-broker-name">{guide.name}</h2>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-2" data-testid="wizard-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 py-3 bg-slate-950/50">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className={`h-1.5 w-12 rounded-full transition-all ${step >= n ? 'bg-amber-500' : 'bg-white/10'}`} />
          ))}
        </div>

        <div className="p-6 space-y-4">
          {/* STEP 1 — Guide */}
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-1">
              {guide.warning && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-start gap-2 text-sm text-amber-200">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {guide.warning}
                </div>
              )}
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 flex items-center gap-3">
                <Lock className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <p className="text-sm text-emerald-200/90">All keys are encrypted at rest (Fernet AES-128). We never expose your secret in any response.</p>
              </div>

              <h3 className="text-lg font-bold">How to generate your {guide.name} API key</h3>
              <ol className="space-y-2.5">
                {guide.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 items-start" data-testid={`wizard-step-line-${i}`}>
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                    <span className="text-sm text-slate-200 leading-relaxed pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>

              {guide.docs_url && (
                <a href={guide.docs_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 border border-white/10 rounded-xl text-sm font-semibold text-white transition" data-testid="wizard-docs-link">
                  <ExternalLink className="w-4 h-4" />
                  Open {guide.name} API dashboard
                </a>
              )}

              <div className="flex justify-end pt-2">
                <button onClick={() => setStep(2)} className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold flex items-center gap-2" data-testid="wizard-next">
                  I have my keys — continue <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — Paste keys */}
          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-step-2">
              <h3 className="text-lg font-bold">Paste your {guide.name} keys</h3>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Label (optional)</label>
                <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                  placeholder={`My ${guide.name} account`}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="wizard-label" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">{guide.field_labels?.api_key || 'API Key'}</label>
                <input type="password" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 font-mono" data-testid="wizard-api-key" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">{guide.field_labels?.api_secret || 'API Secret'}</label>
                <input type="password" value={form.api_secret} onChange={e => setForm({ ...form, api_secret: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 font-mono" data-testid="wizard-api-secret" />
              </div>
              {requiresPassphrase && (
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Passphrase</label>
                  <input type="password" value={form.passphrase} onChange={e => setForm({ ...form, passphrase: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 font-mono" data-testid="wizard-passphrase" />
                </div>
              )}
              {guide.supports_testnet && (
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={form.is_testnet} onChange={e => setForm({ ...form, is_testnet: e.target.checked })}
                    className="w-4 h-4" data-testid="wizard-testnet" />
                  Use <b>Testnet</b> (recommended for first-time setup)
                </label>
              )}
              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(1)} className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm flex items-center gap-1" data-testid="wizard-back">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={handleSave} className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold flex items-center gap-2" data-testid="wizard-save">
                  Save & Validate <ShieldCheck className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — Validating */}
          {step === 3 && (
            <div className="text-center py-10" data-testid="wizard-step-3">
              <Loader2 className="w-12 h-12 text-amber-500 mx-auto animate-spin mb-4" />
              <p className="text-lg font-semibold">Connecting to {guide.name}...</p>
              <p className="text-sm text-slate-400 mt-1">Encrypting + validating your keys</p>
            </div>
          )}

          {/* STEP 4 — Success */}
          {step === 4 && (
            <div className="text-center py-6 space-y-4" data-testid="wizard-step-4">
              <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-r ${(validation?.success || validation?.status === 'saved') ? 'from-emerald-500 to-teal-500' : 'from-red-500 to-rose-500'} flex items-center justify-center`}>
                {(validation?.success || validation?.status === 'saved') ? (
                  <ShieldCheck className="w-10 h-10 text-white" />
                ) : (
                  <AlertTriangle className="w-10 h-10 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-2xl font-bold" data-testid="wizard-result-title">
                  {(validation?.success || validation?.status === 'saved') ? '🎉 Ready to Trade!' : 'Saved with Warning'}
                </h3>
                <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto" data-testid="wizard-result-message">{validation?.message || 'Keys saved.'}</p>
              </div>
              {validation?.details && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-left text-xs space-y-1 max-w-md mx-auto" data-testid="wizard-result-details">
                  {validation.details.canTrade !== undefined && <div>Can Trade: <b>{String(validation.details.canTrade)}</b></div>}
                  {validation.details.accountType && <div>Account: <b>{validation.details.accountType}</b></div>}
                  {validation.details.non_zero_balances_count !== undefined && <div>Non-zero balances: <b>{validation.details.non_zero_balances_count}</b></div>}
                </div>
              )}
              <button onClick={handleFinish} className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-semibold" data-testid="wizard-finish">
                Done — back to API Vault
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
